import * as T from 'three';
import { taperedTube } from './branch-shape';

/**
 * Hero tree species (Design Bible §7 "memorable silhouette, massive trunk", §8 biomes). Every
 * trunk in the world data carries a `species` and a `variant` seed (tools/world-design.mjs); the
 * renderer grows it into a recognisable type: pale kapok giants on plank buttresses with flat
 * umbrella crowns, red fluted mahogany, braided strangler figs with aerial root fringes, banyans
 * dropping prop roots from their limbs, flowering trees with orchid-pink / mango-orange crowns
 * and stilt-rooted mangroves. Geometry here is vertex-coloured and merged by the caller.
 */
export type Species = 'kapok' | 'mahogany' | 'strangler' | 'banyan' | 'flowering' | 'mangrove';
export type CrownShape = 'umbrella' | 'dome' | 'dense' | 'spread' | 'round';
export type SpeciesProfile = {
    /** Bark base and groove colours (Bible §3: reddish bark, value-varied per species). */
    bark: string; dark: string;
    /** Moss coverage on the shaded side, pale lichen spotting. */
    moss: number; lichen: number;
    /** Root flare strength and height (m). */
    flare: number; flareHeight: number;
    /** Vertical ridges: count around the trunk and relative depth. */
    ridges: number; ridgeDepth: number;
    /** Lean of the crown section above the climbable span (m). */
    lean: number;
    base: 'planks' | 'roots' | 'stilts';
    crown: CrownShape;
    /** Crown hues; flowering trees pick one blossom hue per tree. */
    hues: string[];
    /** Structural limb count range, their rise (slope) and length factor. */
    limbs: [number, number]; limbRise: number; limbLength: number;
    /** Chance of decorative lianas hugging the trunk. */
    lianas: number;
    /** Strangler lattice, banyan prop roots, strangler aerial-root fringe, blossoms. */
    lattice?: boolean; props?: boolean; fringe?: boolean; blossom?: boolean;
    /** Crown size factor. */
    size: number;
};
export const SPECIES: Record<Species, SpeciesProfile> = {
    kapok: { bark: '#b19f80', dark: '#6f6150', moss: .3, lichen: .55, flare: 1.15, flareHeight: 7, ridges: 6, ridgeDepth: .035, lean: .8, base: 'planks', crown: 'umbrella', hues: ['#9dc24a', '#8fbb43', '#a9c84e'], limbs: [4, 6], limbRise: .14, limbLength: 1.1, lianas: .55, size: 1.25 },
    mahogany: { bark: '#8f4a32', dark: '#55281c', moss: .55, lichen: .12, flare: .8, flareHeight: 5, ridges: 9, ridgeDepth: .085, lean: 1.2, base: 'roots', crown: 'dome', hues: ['#3f8f4a', '#4b9a4f', '#55a047'], limbs: [3, 5], limbRise: .5, limbLength: .9, lianas: .6, size: 1 },
    strangler: { bark: '#9b9474', dark: '#40342a', moss: .65, lichen: .25, flare: .9, flareHeight: 6, ridges: 5, ridgeDepth: .05, lean: .6, base: 'roots', crown: 'dense', hues: ['#3b8250', '#468a4c', '#367c4e'], limbs: [3, 4], limbRise: .3, limbLength: .9, lianas: .3, lattice: true, fringe: true, size: 1.05 },
    banyan: { bark: '#8e7861', dark: '#5a4838', moss: .5, lichen: .3, flare: 1, flareHeight: 6, ridges: 5, ridgeDepth: .13, lean: .7, base: 'roots', crown: 'spread', hues: ['#5aa443', '#4f9c45', '#68ab3f'], limbs: [4, 6], limbRise: .16, limbLength: 1.15, lianas: .4, props: true, size: 1.15 },
    flowering: { bark: '#9a8170', dark: '#5e4b3c', moss: .3, lichen: .65, flare: .7, flareHeight: 4.5, ridges: 4, ridgeDepth: .03, lean: 1.8, base: 'roots', crown: 'round', hues: ['#e892b8', '#f2a24e', '#c9a0ee', '#f4c84f'], limbs: [3, 4], limbRise: .42, limbLength: .9, lianas: .2, blossom: true, size: .95 },
    mangrove: { bark: '#76604b', dark: '#453527', moss: .5, lichen: .3, flare: .45, flareHeight: 3, ridges: 4, ridgeDepth: .05, lean: 1, base: 'stilts', crown: 'round', hues: ['#7fb04a', '#8bb84f', '#74a845'], limbs: [2, 4], limbRise: .35, limbLength: .8, lianas: .2, size: .9 },
};
/** Region mixes for trees the map does not name (decorative rings, editor-placed trunks). */
const BIOME_MIX: Record<string, Partial<Record<Species, number>>> = {
    gold: { kapok: 4, mahogany: 3, flowering: 2, banyan: 1 },
    village: { banyan: 4, mahogany: 3, flowering: 2, kapok: 1 },
    gorge: { banyan: 3, mahogany: 3, strangler: 2, flowering: 1 },
    temple: { strangler: 5, banyan: 3, kapok: 2 },
    mangrove: { mangrove: 5, banyan: 3, strangler: 1 },
    floor: { kapok: 4, strangler: 3, mahogany: 3 },
    grotto: { strangler: 3, mahogany: 3, flowering: 3 },
    cloud: { kapok: 5, flowering: 2, mahogany: 2 },
    basin: { mahogany: 4, flowering: 3, kapok: 2 },
};
export function speciesForBiome(biome: string, u: number): Species {
    const mix = Object.entries(BIOME_MIX[biome] ?? BIOME_MIX.gold) as [Species, number][];
    let left = u * mix.reduce((a, [, w]) => a + w, 0);
    for (const [name, w] of mix) if ((left -= w) < 0) return name;
    return mix[0][0];
}
export const isSpecies = (s: unknown): s is Species => typeof s === 'string' && s in SPECIES;

/**
 * Shaded side of every trunk (moss grows there). The key light in main.ts sits at (-35, 80, -35),
 * so bark facing +x/+z is in shade.
 */
export const SHADE = new T.Vector2(Math.SQRT1_2, Math.SQRT1_2);
const smooth = (x: number, a: number, b: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const MOSS = new T.Color('#7f9c3f'), WET = new T.Color('#4a3d2f'), LICHEN = new T.Color('#d9d5bd');

export type TrunkShape = { geometry: T.BufferGeometry; curve: T.CatmullRomCurve3; radiusAt: (y: number) => number; centerAt: (y: number, out?: T.Vector3) => T.Vector3; bark: T.Color; dark: T.Color };
/**
 * A species trunk: straight (≤ 0.25 m of drift) and collider-sized through the climbable span,
 * flared at the roots, ridged/fluted per species, with knots, a moss side facing away from the sun,
 * a dark wet band at the waterline and lichen spots; above the span it leans and tapers into the crown.
 */
/** Per-tree bark colours from the species and the tree's variant seed. */
export function barkColors(p: SpeciesProfile, variant: number) {
    const u = (k: number) => { const x = Math.sin(variant * 12.9898 + k * 78.233) * 43758.5453; return x - Math.floor(x) - .5; };
    return { bark: new T.Color(p.bark).offsetHSL(u(1) * .03, u(2) * .14, u(3) * .09), dark: new T.Color(p.dark).offsetHSL(0, 0, u(4) * .05) };
}
export function trunkShape(x: number, z: number, bottom: number, climbTop: number, top: number, r: number, p: SpeciesProfile, random: () => number, bark: T.Color, dark: T.Color): TrunkShape {
    const leanAngle = random() * Math.PI * 2, lean = new T.Vector3(Math.cos(leanAngle), 0, Math.sin(leanAngle)).multiplyScalar(p.lean * (.5 + random() * .5));
    const drift = new T.Vector3(Math.cos(leanAngle + 1.7), 0, Math.sin(leanAngle + 1.7)).multiplyScalar(Math.min(.25, r * .12));
    const pts = [
        new T.Vector3(x, bottom, z),
        new T.Vector3(x + drift.x, T.MathUtils.lerp(bottom, climbTop, .5), z + drift.z),
        new T.Vector3(x, climbTop, z),
        new T.Vector3(x + lean.x * .35, (climbTop + top) / 2, z + lean.z * .35),
        new T.Vector3(x + lean.x, top, z + lean.z),
    ];
    const curve = new T.CatmullRomCurve3(pts, false, 'centripetal');
    const segments = Math.max(12, Math.ceil((top - bottom) / 2.1)), radial = r > 1.4 ? 14 : 11;
    const g = new T.TubeGeometry(curve, segments, 1, radial, false);
    const pos = g.getAttribute('position'), center = new T.Vector3(), point = new T.Vector3();
    const phase = random() * 6, twist = (random() - .5) * .06;
    const knots = Array.from({ length: 3 + Math.floor(random() * 4) }, () => ({ a: random() * Math.PI * 2, y: T.MathUtils.lerp(bottom + 4, climbTop, random()), s: .7 + random() * .6 }));
    const radiusAt = (y: number) => {
        const flare = 1 + p.flare * Math.max(0, 1 - (y - bottom - 1) / p.flareHeight) ** 2;
        const taper = y <= climbTop ? 1 : T.MathUtils.lerp(1, .14, ((y - climbTop) / Math.max(1, top - climbTop)) ** 1.25);
        return r * flare * taper;
    };
    const colors = new Float32Array(pos.count * 3), c = new T.Color();
    for (let i = 0; i <= segments; i++) {
        curve.getPointAt(i / segments, center);
        const y = center.y, base = radiusAt(y);
        for (let j = 0; j <= radial; j++) {
            const ix = i * (radial + 1) + j;
            point.fromBufferAttribute(pos, ix).sub(center);
            const a = Math.atan2(point.z, point.x);
            const groove = Math.sin(a * p.ridges + y * twist + phase);
            let knot = 0;
            for (const k of knots) { const da = Math.atan2(Math.sin(a - k.a), Math.cos(a - k.a)); knot += .16 * k.s * Math.exp(-(da * da) / .05 - (y - k.y) ** 2 / (.35 * k.s)); }
            const ridge = 1 + p.ridgeDepth * groove + .02 * Math.sin(a * 13 + y * .7 + phase) + knot;
            point.multiplyScalar(base * ridge).add(center);
            pos.setXYZ(ix, point.x, point.y, point.z);
            // Bark: grooves darker, knots darker still, moss on the shaded side, lichen, wet base.
            c.copy(bark).lerp(dark, Math.max(0, -groove) * .62 + Math.min(.6, knot * 3));
            const shaded = smooth(Math.cos(a) * SHADE.x + Math.sin(a) * SHADE.y, -.1, .75);
            const patch = .55 + .45 * Math.sin(y * .55 + a * 2 + phase) * Math.sin(y * .23 - a + phase * 2);
            c.lerp(MOSS, Math.min(.85, shaded * patch * p.moss * 1.25 + smooth(bottom + p.flareHeight - y, -1, 3) * .3 * p.moss));
            if (p.lichen && Math.sin(a * 5.3 + y * 1.9 + phase) * Math.sin(a * 3.1 - y * 1.3) > 1 - p.lichen * .35) c.lerp(LICHEN, .4);
            if (y < 1.2) c.lerp(WET, smooth(1.2 - y, 0, 2.5) * .7);
            c.multiplyScalar(.93 + .07 * Math.sin(y * .5 + a * 3 + phase));
            colors[ix * 3] = c.r; colors[ix * 3 + 1] = c.g; colors[ix * 3 + 2] = c.b;
        }
    }
    g.setAttribute('color', new T.BufferAttribute(colors, 3));
    g.computeVertexNormals();
    // The axis rises monotonically, so a sampled table answers "where is the trunk at height y".
    const table = curve.getSpacedPoints(64);
    const centerAt = (y: number, out = new T.Vector3()) => {
        if (y <= table[0].y) return out.copy(table[0]).setY(y);
        for (let i = 1; i < table.length; i++)
            if (table[i].y >= y) return out.copy(table[i - 1]).lerp(table[i], (y - table[i - 1].y) / Math.max(1e-6, table[i].y - table[i - 1].y));
        return out.copy(table[table.length - 1]).setY(y);
    };
    return { geometry: g, curve, radiusAt, centerAt, bark, dark };
}

/** Paints a tube laid out as (segments+1)×(radial+1) with a callback per ring. */
export function paintRings(g: T.BufferGeometry, segments: number, radial: number, paint: (u: number, j: number, out: T.Color) => void) {
    const n = g.getAttribute('position').count, colors = new Float32Array(n * 3), c = new T.Color();
    for (let i = 0; i <= segments; i++)
        for (let j = 0; j <= radial; j++) {
            const ix = i * (radial + 1) + j;
            paint(i / segments, j, c);
            colors[ix * 3] = c.r; colors[ix * 3 + 1] = c.g; colors[ix * 3 + 2] = c.b;
        }
    g.setAttribute('color', new T.BufferAttribute(colors, 3));
    return g;
}

/** A vertex-coloured tapered tube along points (centripetal Catmull-Rom). */
export function barkTube(points: T.Vector3[], r0: number, r1: number, color: T.Color, dark: T.Color, radial = 7, profile?: (u: number) => number) {
    const curve = new T.CatmullRomCurve3(points, false, 'centripetal');
    const segments = Math.max(4, Math.ceil(curve.getLength() * .95));
    const g = taperedTube(curve, segments, radial, profile ?? (u => T.MathUtils.lerp(r0, r1, u)));
    g.computeVertexNormals();
    const normal = g.getAttribute('normal');
    paintRings(g, segments, radial, (u, j, c) => {
        const ny = normal.getY(Math.round(u * segments) * (radial + 1) + j);
        c.copy(color).lerp(dark, (j % 2) * .15).lerp(MOSS, smooth(ny, .45, .9) * .55);
    });
    return g;
}

/**
 * Kapok plank buttresses: thin flaring fins that start high on the trunk and sweep out to the water,
 * concave outer edge, thicker where they join the bark.
 */
export function plankButtresses(x: number, z: number, r: number, bottom: number, color: T.Color, dark: T.Color, random: () => number) {
    const parts: T.BufferGeometry[] = [], n = 5 + Math.floor(random() * 3), turn = random() * Math.PI * 2;
    for (let f = 0; f < n; f++) {
        const angle = turn + f / n * Math.PI * 2 + (random() - .5) * .4, d = new T.Vector3(Math.cos(angle), 0, Math.sin(angle)), side = new T.Vector3(-d.z, 0, d.x);
        const reach = r * (2.4 + random() * 1.3), height = 4 + r * 1.6 + random() * 2.5, thick = r * (.2 + random() * .07);
        const U = 9, V = 4, positions: number[] = [], colors: number[] = [], index: number[] = [], c = new T.Color();
        // u: 0 at the water → 1 high on the trunk; v: 0 inside the trunk → 1 on the outer edge.
        const outer = (u: number) => r * .9 + (reach - r * .9) * (1 - u) ** 2.4;
        for (const face of [-1, 1])
            for (let i = 0; i <= U; i++) {
                const u = i / U, y = bottom + u * (height - bottom + 1), edge = outer(u);
                for (let j = 0; j <= V; j++) {
                    const v = j / V, along = T.MathUtils.lerp(r * .45, edge, v), t = thick * (1 - v * .8) * (1 - u * .5);
                    const sag = Math.sin(v * Math.PI) * .12 * (1 - u);
                    positions.push(x + d.x * along + side.x * t * face, y - sag, z + d.z * along + side.z * t * face);
                    c.copy(color).lerp(dark, (1 - v) * .25 + (j % 2) * .06).lerp(MOSS, smooth(1 - u, .4, 1) * .35 * (face > 0 ? 1 : .5));
                    if (y < 1) c.lerp(WET, .55);
                    colors.push(c.r, c.g, c.b);
                }
            }
        const row = V + 1, sheet = (U + 1) * row;
        for (const [face, flip] of [[0, false], [1, true]] as const)
            for (let i = 0; i < U; i++)
                for (let j = 0; j < V; j++) {
                    const a = face * sheet + i * row + j, b = a + row;
                    if (flip) index.push(a, a + 1, b, a + 1, b + 1, b); else index.push(a, b, a + 1, a + 1, b, b + 1);
                }
        // Outer rim joins the two faces.
        for (let i = 0; i < U; i++) {
            const a = i * row + V, b = (i + 1) * row + V, a2 = sheet + a, b2 = sheet + b;
            index.push(a, b, a2, a2, b, b2);
        }
        const g = new T.BufferGeometry();
        g.setAttribute('position', new T.Float32BufferAttribute(positions, 3));
        g.setAttribute('color', new T.Float32BufferAttribute(colors, 3));
        g.setIndex(index);
        g.computeVertexNormals();
        parts.push(g.toNonIndexed());
        g.dispose();
    }
    return parts;
}

/**
 * Strangler-fig lattice: braided root strands wrapping the host trunk, merging and parting as they
 * climb, so the trunk reads as woven rather than a cylinder. Slightly proud of the collider.
 */
export function strangleStrands(centerAt: TrunkShape['centerAt'], radiusAt: (y: number) => number, bottom: number, climbTop: number, color: T.Color, dark: T.Color, random: () => number) {
    const parts: T.BufferGeometry[] = [], n = 6 + Math.floor(random() * 3), turn = random() * 6, spin = (random() < .5 ? -1 : 1) * (.05 + random() * .04);
    const center = new T.Vector3();
    for (let s = 0; s < n; s++) {
        const a0 = turn + s / n * Math.PI * 2, wobble = random() * 6, pts: T.Vector3[] = [];
        const top = climbTop + 1 + random() * 3, steps = Math.ceil((top - bottom) / 1.6);
        for (let k = 0; k <= steps; k++) {
            const y = bottom + (top - bottom) * k / steps;
            centerAt(y, center);
            const a = a0 + y * spin + .45 * Math.sin(y * .32 + wobble), rr = radiusAt(y) * .97;
            pts.push(new T.Vector3(center.x + Math.cos(a) * rr, y, center.z + Math.sin(a) * rr));
        }
        const thick = radiusAt(bottom + 6) * (.13 + random() * .06);
        const g = barkTube(pts, thick * 1.5, thick * .55, color.clone().offsetHSL(0, 0, (random() - .5) * .06), dark, 5);
        parts.push(g);
    }
    return parts;
}

/** A decorative liana spiralling tightly around a trunk: thin, hugging the bark, never catchable. */
export function trunkLiana(centerAt: TrunkShape['centerAt'], radiusAt: (y: number) => number, bottom: number, top: number, random: () => number) {
    const pts: T.Vector3[] = [], turns = 1.2 + random() * 1.5, a0 = random() * 6, center = new T.Vector3();
    const y0 = Math.max(bottom, .5), steps = 26;
    for (let k = 0; k <= steps; k++) {
        const u = k / steps, y = T.MathUtils.lerp(y0, top, u);
        centerAt(y, center);
        const a = a0 + u * turns * Math.PI * 2, rr = radiusAt(y) * 1.07 + .04;
        pts.push(new T.Vector3(center.x + Math.cos(a) * rr, y, center.z + Math.sin(a) * rr));
    }
    const color = new T.Color('#6f6b3c').offsetHSL(0, 0, (random() - .5) * .05);
    return barkTube(pts, .085, .05, color, new T.Color('#4f4c2a'), 5);
}

/** Banyan prop root / mangrove stilt: a pillar from a limb (or the lower trunk) down into the water. */
export function rootPillar(from: T.Vector3, to: T.Vector3, r: number, color: T.Color, dark: T.Color, arch: number, random: () => number) {
    const mid = from.clone().lerp(to, .45), out = to.clone().sub(from).setY(0);
    if (out.lengthSq() > 1e-4) mid.addScaledVector(out.normalize(), arch);
    mid.y += arch * .6;
    const wobble = new T.Vector3((random() - .5) * .3, 0, (random() - .5) * .3);
    return barkTube([from, mid.add(wobble), to], r * .7, r * 1.15, color, dark, 6, u => r * (.7 + .45 * u + .25 * Math.max(0, u - .85) / .15));
}
